import { useEffect } from 'react';
import { useFlag } from '@unleash/proxy-client-react';

import { appendAppBanner, removeAppBanner } from 'utils/appendScript';
import { FeatureFlag } from 'utils/enums';

/**
 * Single predicate for whether web-originated app-install prompts (Safari's Smart App Banner
 * today) should be suppressed. Kept as its own function — rather than inlined in the effect below
 * — so that narrowing suppression later (e.g. to `documentType=state-pharmacy-terms`, or to a
 * specific route) is a one-line edit instead of a hunt across call sites. Suppression is global,
 * not page-scoped: the banner is appended on `/login`, before any consent-flow marker survives
 * into the URL, so a route- or param-keyed check cannot be built reliably today. See LMD-1472.
 */
const shouldSuppressAppInstallPrompts = (isHideAppInstallPromptsEnabled: boolean): boolean =>
  isHideAppInstallPromptsEnabled;

/**
 * Owns the decision of whether to show or suppress web-originated app-install prompts, and
 * applies it as a DOM effect.
 *
 * `shouldShowAppBanner` is the caller's own eligibility rule for the banner (e.g. `Router`
 * excludes signup routes and renders that are about to redirect) and is respected exactly when
 * the feature flag is off, so flag-off behavior is unchanged. When the flag is on, suppression
 * wins regardless of `shouldShowAppBanner`, because suppression must be global.
 *
 * The DOM mutation runs from an effect rather than during render, replacing the side-effect-in-
 * render `appendAppBanner()` call this hook was extracted from. Suppression itself is decided
 * before first paint because `AppRouter` already blocks render on `flagsReady`
 * (`src/router/index.tsx`), so the flag value read here is never in an unresolved state — there is
 * no window in which the banner is appended and then removed.
 *
 * Note this hook does not own every path that touches the meta tag: signup routes render outside
 * the `Router` layout (`getSignupRoutes()` is a sibling of `<Route element={<Router />}>`), so the
 * separate `document.querySelector('meta[name="apple-itunes-app"]')?.remove()` in
 * `useOnboarding.ts` still stands on its own and is unaffected by this flag.
 */
export const useAppInstallPrompts = (shouldShowAppBanner: boolean) => {
  const isHideAppInstallPromptsEnabled = useFlag(FeatureFlag.HideAppInstallPrompts);
  const isSuppressed = shouldSuppressAppInstallPrompts(isHideAppInstallPromptsEnabled);

  useEffect(() => {
    if (isSuppressed) {
      removeAppBanner();
      return;
    }

    if (shouldShowAppBanner) {
      appendAppBanner();
    }
  }, [isSuppressed, shouldShowAppBanner]);
};
